// ============================================================================
// Editor de tienda — catálogo de opciones (temas, headers, estilos) y su
// gating por plan.
//
// IMPORTANTE: no se inventa funcionalidad comercial. Cada variante se
// desbloquea con una FEATURE EXISTENTE de la matriz de planes
// (plan_feature). Las claves aquí reutilizan las del seed de planes:
//   - banner / logo / favicon ... base (Prueba y Básico)
//   - temas_varios, cabecera_avanzada, estilo_botones_avanzado,
//     estilo_tarjetas_avanzado, tipografias_avanzadas, orden_secciones,
//     secciones_portada_avanzadas ... Medio
//   - quitar_branding ... Pro
// ============================================================================

import { iconoCandado } from '@plataforma/ui';

// ---------------------------------------------------------------------------
// Variantes de header
// ---------------------------------------------------------------------------
// Cada variante define cómo se renderiza el header de la preview.
// `plan` indica la feature que la desbloquea (null = base, siempre disponible).
export const HEADER_VARIANTES = [
  {
    id: 'clasico',
    nombre: 'Clásico',
    descripcion:
      'Universal y equilibrado: logo centrado, navegación horizontal, acciones a la derecha.',
    plan: null,
    caracteristicas: {
      alineacion: 'centro',
      menu: 'lineal',
      fondo_solido: true,
      whatsapp: true,
      estructura: 'centrado',
      busqueda: false,
      barraCategorias: false,
      navConIconos: true,
    },
  },
  {
    id: 'ecommerce',
    nombre: 'Ecommerce',
    descripcion: 'Orientado a venta: búsqueda accesible, categorías visibles, carrito prominente.',
    plan: 'cabecera_avanzada',
    caracteristicas: {
      alineacion: 'izquierda',
      menu: 'categorias',
      fondo_solido: true,
      buscador: true,
      whatsapp: true,
      estructura: 'busqueda-lateral',
      busqueda: true,
      barraCategorias: true,
      navConIconos: false,
      busquedaExpandible: true,
    },
  },
  {
    id: 'premium',
    nombre: 'Premium',
    descripcion:
      'Sofisticado: logo grande, navegación integrada, transparente sobre hero, scroll-aware.',
    plan: 'cabecera_avanzada',
    caracteristicas: {
      alineacion: 'izquierda',
      menu: 'lineal',
      fondo_solido: false,
      whatsapp: true,
      estructura: 'hero-integrado',
      busqueda: false,
      barraCategorias: false,
      navConIconos: false,
      transparente: true,
      logoGrande: true,
      scrollAware: true,
      accionesDiscretas: true,
    },
  },
  {
    id: 'minimal',
    nombre: 'Minimal',
    descripcion:
      'Essencial: máximo espacio, solo lo necesario, logo protagonista, acciones por iconos.',
    plan: 'cabecera_avanzada',
    caracteristicas: {
      alineacion: 'izquierda',
      menu: 'minimal',
      fondo_solido: false,
      whatsapp: false,
      estructura: 'minimal',
      busqueda: false,
      barraCategorias: false,
      navConIconos: false,
      soloEsenciales: true,
      muchoEspacio: true,
    },
  },
  {
    id: 'moderno',
    nombre: 'Moderno',
    descripcion:
      'Audaz y contemporáneo: franja útil arriba, marca en negrita, navegación con iconos y acciones claras.',
    plan: 'cabecera_avanzada',
    caracteristicas: {
      alineacion: 'izquierda',
      menu: 'lineal',
      fondo_solido: true,
      whatsapp: false,
      estructura: 'doble-plano',
      busqueda: false,
      barraCategorias: false,
      navConIconos: true,
      franjaUtil: true,
      bold: true,
    },
  },
  {
    id: 'marketplace',
    nombre: 'Marketplace',
    descripcion:
      'Gran mercado: franja oscura, búsqueda amplia y barra gruesa de categorías para escanear y comprar.',
    plan: 'cabecera_avanzada',
    caracteristicas: {
      alineacion: 'izquierda',
      menu: 'categorias',
      fondo_solido: true,
      buscador: true,
      whatsapp: false,
      estructura: 'multi-capa',
      busqueda: true,
      barraCategorias: true,
      navConIconos: false,
      franjaOscura: true,
      busquedaAmplia: true,
    },
  },
  {
    id: 'editorial',
    nombre: 'Editorial',
    descripcion:
      'Estilo revista: cabecera alta, navegación versalitas sobre segunda línea, hairlines finas.',
    plan: 'personalizacion_avanzada',
    caracteristicas: {
      alineacion: 'izquierda',
      menu: 'lineal',
      fondo_solido: true,
      whatsapp: false,
      estructura: 'revista',
      busqueda: false,
      barraCategorias: false,
      navConIconos: false,
      alto: true,
      versales: true,
    },
  },
  {
    id: 'flotante',
    nombre: 'Flotante',
    descripcion:
      'Cristal: transparente sobre el hero con el contenido en una pastilla flotante con sombra y blur.',
    plan: 'personalizacion_avanzada',
    caracteristicas: {
      alineacion: 'izquierda',
      menu: 'lineal',
      fondo_solido: false,
      whatsapp: false,
      estructura: 'pastilla',
      busqueda: false,
      barraCategorias: false,
      navConIconos: false,
      transparente: true,
      flotante: true,
      scrollAware: true,
    },
  },
];

// ---------------------------------------------------------------------------
// Variantes de categorías destacadas
// ---------------------------------------------------------------------------
// Cada variante es una COMPOSICIÓN visual distinta de las tarjetas de
// categoría (no un simple cambio de columnas). El markup de la tarjeta es
// siempre el mismo (ver storefront `home.js#bloqueCategorias`); lo que cambia
// es el contenedor `sp-categorias--{id}` y los modificadores
// `sp-categoria--protagonista`/`sp-categoria--secundaria` que engancha el
// CSS para componer cada layout.
// `plan` indica la feature que la desbloquea (null = base, siempre disponible).
export const VARIANTES_CATEGORIAS = [
  {
    id: 'clasico',
    nombre: 'Clásico',
    descripcion: 'Grid equilibrado de tarjetas uniformes.',
    plan: null,
    caracteristicas: { protagonista: false, compacto: false, horizontal: false, jerarquia: false },
  },
  {
    id: 'editorial',
    nombre: 'Editorial',
    descripcion: 'Una categoría protagonista grande y otras secundarias.',
    plan: null,
    caracteristicas: { protagonista: true, compacto: false, horizontal: false, jerarquia: true },
  },
  {
    id: 'mosaico',
    nombre: 'Mosaico',
    descripcion: 'Composición tipo masonry controlado, con alturas firmes y sin mosaico aleatorio.',
    plan: null,
    caracteristicas: { protagonista: false, compacto: false, horizontal: false, jerarquia: false },
  },
  {
    id: 'panorama',
    nombre: 'Panorama',
    descripcion: 'Una categoría horizontal protagonista y el resto en grid debajo.',
    plan: null,
    caracteristicas: {
      protagonista: true,
      panorama: true,
      compacto: false,
      horizontal: false,
      jerarquia: true,
    },
  },
  {
    id: 'compacto',
    nombre: 'Compacto',
    descripcion: 'Más densidad visual con tarjetas uniformes y limpias.',
    plan: null,
    caracteristicas: { protagonista: false, compacto: true, horizontal: false, jerarquia: false },
  },
  {
    id: 'horizontal',
    nombre: 'Horizontal',
    descripcion: 'Tarjetas panorámicas con desplazamiento horizontal elegante.',
    plan: null,
    caracteristicas: { protagonista: false, compacto: false, horizontal: true, jerarquia: false },
  },
  {
    id: 'magazine',
    nombre: 'Magazine',
    descripcion: 'Composición editorial moderna con jerarquía visual marcada.',
    plan: null,
    caracteristicas: {
      protagonista: true,
      grande: true,
      compacto: false,
      horizontal: false,
      jerarquia: true,
    },
  },
  {
    id: 'minimal',
    nombre: 'Minimal',
    descripcion: 'Composición limpia, mucho aire, imágenes protagonistas y texto elegante.',
    plan: null,
    caracteristicas: {
      protagonista: false,
      minimal: true,
      compacto: false,
      horizontal: false,
      jerarquia: false,
    },
  },
];

// ---------------------------------------------------------------------------
// Variantes de presentación de Propuesta de valor
// ---------------------------------------------------------------------------
// Estructuras visuales realmente distintas sobre los mismos puntos de
// confianza (contenido administrado en "Página principal"). `plan: null` =
// base, siempre disponible (coherente con "no bloquear variantes
// arbitrariamente"; el gating por plan ya lo gobiernan header/categorías).
export const VARIANTES_PROPUESTA = [
  {
    id: 'banda',
    nombre: 'Banda',
    descripcion: 'Los puntos en una franja horizontal continua.',
    plan: null,
    caracteristicas: { tarjetas: false, principal: false, minimal: false },
  },
  {
    id: 'tarjetas',
    nombre: 'Tarjetas',
    descripcion: 'Cada punto en su propia tarjeta con icono.',
    plan: null,
    caracteristicas: { tarjetas: true, principal: false, minimal: false },
  },
  {
    id: 'editorial',
    nombre: 'Editorial',
    descripcion: 'Titular destacado y puntos en dos columnas.',
    plan: null,
    caracteristicas: { tarjetas: false, principal: true, minimal: false },
  },
  {
    id: 'minimal',
    nombre: 'Minimal',
    descripcion: 'Línea discreta y centrada, solo lo esencial.',
    plan: null,
    caracteristicas: { tarjetas: false, principal: false, minimal: true },
  },
];

// ---------------------------------------------------------------------------
// Variantes de distribución de Productos destacados
// ---------------------------------------------------------------------------
// Cada variante compone la rejilla de tarjetas de producto (mismo markup de
// `tarjetaProductoHtml`) de forma estructuralmente distinta.
export const VARIANTES_PRODUCTOS = [
  {
    id: 'clasico',
    nombre: 'Grid clásico',
    descripcion: 'Rejilla equilibrada de tarjetas uniformes.',
    plan: null,
    caracteristicas: { lista: false, compacto: false, destacado: false },
  },
  {
    id: 'lista',
    nombre: 'Lista',
    descripcion: 'Filas horizontales con imagen a la izquierda.',
    plan: null,
    caracteristicas: { lista: true, compacto: false, destacado: false },
  },
  {
    id: 'compacto',
    nombre: 'Compacto',
    descripcion: 'Más densidad y columnas, ideal para catálogos largos.',
    plan: null,
    caracteristicas: { lista: false, compacto: true, destacado: false },
  },
  {
    id: 'destacado',
    nombre: 'Destacado',
    descripcion: 'Un producto protagonista grande y el resto en rejilla.',
    plan: null,
    caracteristicas: { lista: false, compacto: false, destacado: true },
  },
];

// ---------------------------------------------------------------------------
// Variantes de distribución de Testimonios
// ---------------------------------------------------------------------------
export const VARIANTES_TESTIMONIOS = [
  {
    id: 'tarjetas',
    nombre: 'Tarjetas',
    descripcion: 'Tarjetas uniformes en rejilla.',
    plan: null,
    caracteristicas: { central: false, minimal: false, carrusel: false },
  },
  {
    id: 'central',
    nombre: 'Central',
    descripcion: 'Uno protagonista en el centro, el resto debajo.',
    plan: null,
    caracteristicas: { central: true, minimal: false, carrusel: false },
  },
  {
    id: 'minimal',
    nombre: 'Minimal',
    descripcion: 'Filas limpias con cita y autor.',
    plan: null,
    caracteristicas: { central: false, minimal: true, carrusel: false },
  },
  {
    id: 'carrusel',
    nombre: 'Carrusel',
    descripcion: 'Desplazamiento horizontal elegante.',
    plan: null,
    caracteristicas: { central: false, minimal: false, carrusel: true },
  },
];

// ---------------------------------------------------------------------------
// Variantes de distribución de Contacto
// ---------------------------------------------------------------------------
export const VARIANTES_CONTACTO = [
  {
    id: 'resumen',
    nombre: 'Resumen',
    descripcion: 'Texto a la izquierda y botón a la derecha.',
    plan: null,
    caracteristicas: { centrado: false, tarjeta: false },
  },
  {
    id: 'centrado',
    nombre: 'Centrado',
    descripcion: 'CTA centrado y equilibrado.',
    plan: null,
    caracteristicas: { centrado: true, tarjeta: false },
  },
  {
    id: 'tarjeta',
    nombre: 'Tarjeta',
    descripcion: 'Bloque en tarjeta con botón prominente.',
    plan: null,
    caracteristicas: { centrado: false, tarjeta: true },
  },
];

// ---------------------------------------------------------------------------
// Variantes de presentación del Pie de página
// ---------------------------------------------------------------------------
export const VARIANTES_FOOTER = [
  {
    id: 'clasico',
    nombre: 'Clásico',
    descripcion: 'Marca + columnas de contacto y redes.',
    plan: null,
    caracteristicas: { centrado: false, minimal: false },
  },
  {
    id: 'centrado',
    nombre: 'Centrado',
    descripcion: 'Marca y columnas centradas.',
    plan: null,
    caracteristicas: { centrado: true, minimal: false },
  },
  {
    id: 'minimal',
    nombre: 'Minimal',
    descripcion: 'Solo marca centrada y legal.',
    plan: null,
    caracteristicas: { centrado: true, minimal: true },
  },
];

// ---------------------------------------------------------------------------
// Estilos de botones
// ---------------------------------------------------------------------------
export const ESTILOS_BOTONES = [
  { id: 'redondeado', nombre: 'Redondeado', descripcion: 'Radio medio clásico.', plan: null },
  {
    id: 'cuadrado',
    nombre: 'Cuadrado',
    descripcion: 'Esquinas rectas.',
    plan: 'estilo_botones_avanzado',
  },
  {
    id: 'pill',
    nombre: 'Píldora',
    descripcion: 'Completamente redondeado.',
    plan: 'estilo_botones_avanzado',
  },
  {
    id: 'outline',
    nombre: 'Contorno',
    descripcion: 'Solo borde, sin relleno.',
    plan: 'estilo_botones_avanzado',
  },
];

// ---------------------------------------------------------------------------
// Estilos de tarjetas
// ---------------------------------------------------------------------------
export const ESTILOS_TARJETAS = [
  { id: 'borde', nombre: 'Borde', descripcion: 'Tarjeta con borde sutil.', plan: null },
  {
    id: 'relleno',
    nombre: 'Relleno',
    descripcion: 'Fondo diferenciado.',
    plan: 'estilo_tarjetas_avanzado',
  },
  {
    id: 'elevado',
    nombre: 'Elevado',
    descripcion: 'Con sombra flotante.',
    plan: 'estilo_tarjetas_avanzado',
  },
];

// ---------------------------------------------------------------------------
// Tipografías (tema_visual.tipografia)
// ---------------------------------------------------------------------------
// Las básicas (Inter/Sistema) son del plan Prueba/Básico; las avanzadas
// (Serif/Monospace/Rounded) se desbloquean con la feature existente
// `tipografias_avanzadas` (Medio) — misma feature que ya usa la matriz de
// planes (ver seed 20260101000016_plan_trial_suscripcion.sql).
export const TIPOGRAFIAS_OPCIONES = [
  { id: 'inter', nombre: 'Inter', plan: null },
  { id: 'system', nombre: 'Sistema', plan: null },
  { id: 'serif', nombre: 'Serif', plan: 'tipografias_avanzadas' },
  { id: 'mono', nombre: 'Monospace', plan: 'tipografias_avanzadas' },
  { id: 'rounded', nombre: 'Rounded', plan: 'tipografias_avanzadas' },
];

// ---------------------------------------------------------------------------
// Sombras del tema (tema_visual.sombras)
// ---------------------------------------------------------------------------
// Ninguna/Suave son básicas. Medias/Fuertes se desbloquean con la feature
// existente `estilo_tarjetas_avanzado` (Medio), que ya gobierna las sombras
// de las superficies (tarjetas).
export const SOMBRAS_OPCIONES = [
  { id: 'ninguna', nombre: 'Ninguna', plan: null },
  { id: 'suave', nombre: 'Suave', plan: null },
  { id: 'media', nombre: 'Media', plan: 'estilo_tarjetas_avanzado' },
  { id: 'fuerte', nombre: 'Fuerte', plan: 'estilo_tarjetas_avanzado' },
];

// ---------------------------------------------------------------------------
// Espaciado del tema (tema_visual.espaciado)
// ---------------------------------------------------------------------------
// Compacto/Normal/Amplio son personalización básica: sin feature requerida.
export const ESPACIADOS_OPCIONES = [
  { id: 'compacto', nombre: 'Compacto', plan: null },
  { id: 'normal', nombre: 'Normal', plan: null },
  { id: 'amplio', nombre: 'Amplio', plan: null },
];

// ---------------------------------------------------------------------------
// Catálogos para la Cards del editor visual (ESTILO GENERAL).
//
// SOLO presentación: cada opción reutiliza el mismo `id` (enum) que ya usa el
// storefront (tipografia/sombras/espaciado/radio_bordes/botones/tarjetas) para
// NO romper la compatibilidad con la configuración existente. La nueva
// interfaz es únicamente la forma visual de seleccionarlas.
//
// `plan` está pensado para el bloqueo futuro. Por ahora todas las cards quedan
// disponibles (plan: null) — se deja la estructura lista para activar el
// gating por plan cuando se requiera (ver evaluarBloqueo).
// ---------------------------------------------------------------------------

// Muestra de cada tipografía: familia CSS real + textos de ejemplo.
export const TIPOGRAFIAS_VISUALES = [
  {
    id: 'inter',
    nombre: 'Modern Sans',
    muestra: 'Aa',
    frase: 'Compra lo mejor para ti',
    familia: 'Inter, ui-sans-serif, system-ui, sans-serif',
    plan: null,
  },
  {
    id: 'serif',
    nombre: 'Elegant Serif',
    muestra: 'Aa',
    frase: 'Compra lo mejor para ti',
    familia: "Georgia, 'Times New Roman', serif",
    plan: null,
  },
  {
    id: 'system',
    nombre: 'Clasica',
    muestra: 'Aa',
    frase: 'Compra lo mejor para ti',
    familia: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
    plan: null,
  },
  {
    id: 'rounded',
    nombre: 'Redondeada',
    muestra: 'Aa',
    frase: 'Compra lo mejor para ti',
    familia: "'Nunito Sans', Inter, ui-sans-serif, sans-serif",
    plan: null,
  },
  {
    id: 'mono',
    nombre: 'Monoespaciada',
    muestra: 'Aa',
    frase: 'Compra lo mejor para ti',
    familia: "'SFMono-Regular', Consolas, 'Liberation Mono', monospace",
    plan: null,
  },
];

// Intensidades de sombra: se muestra la elevación real sobre una tarjeta demo.
export const SOMBRAS_VISUALES = [
  {
    id: 'ninguna',
    nombre: 'Sin sombra',
    descripcion: 'Una tarjeta plana sin elevación.',
    css: 'none',
    plan: null,
  },
  {
    id: 'suave',
    nombre: 'Suave',
    descripcion: 'Una tarjeta con sombra ligera.',
    css: '0 6px 14px rgb(16 38 58 / 0.08)',
    plan: null,
  },
  {
    id: 'media',
    nombre: 'Media',
    descripcion: 'Una tarjeta con sombra más visible.',
    css: '0 14px 30px rgb(16 38 58 / 0.16)',
    plan: null,
  },
  {
    id: 'fuerte',
    nombre: 'Elevada',
    descripcion: 'Una tarjeta claramente elevada.',
    css: '0 22px 48px rgb(16 38 58 / 0.26)',
    plan: null,
  },
];

// Densidad de separación vertical entre secciones.
export const ESPACIADOS_VISUALES = [
  {
    id: 'compacto',
    nombre: 'Compacto',
    descripcion: 'Elementos más juntos.',
    densidad: 0.42,
    plan: null,
  },
  {
    id: 'normal',
    nombre: 'Normal',
    descripcion: 'Espaciado equilibrado.',
    densidad: 1,
    plan: null,
  },
  {
    id: 'amplio',
    nombre: 'Amplio',
    descripcion: 'Más aire entre elementos.',
    densidad: 1.7,
    plan: null,
  },
];

// Presets rápidos del radio de bordes (tema_visual.radio_bordes).
export const BORDES_OPCIONES = [
  { id: '0', nombre: 'Recto', etiqueta: '0 px', valor: 0, plan: null },
  { id: '8', nombre: 'Suave', etiqueta: '8 px', valor: 8, plan: null },
  { id: '16', nombre: 'Redondeado', etiqueta: '16 px', valor: 16, plan: null },
  { id: '24', nombre: 'Muy redondeado', etiqueta: '24 px', valor: 24, plan: null },
  { id: '999', nombre: 'Píldora', etiqueta: '999 px', valor: 999, plan: null },
];

// ---------------------------------------------------------------------------
// Presets de tema (paleta de color rápida)
// ---------------------------------------------------------------------------
export const TEMAS_PREDETERMINADOS = [
  {
    id: 'moderno',
    nombre: 'Moderno',
    plan: null,
    paleta: {
      color_primario: '#116b55',
      color_secundario: '#bf3f32',
      color_botones: '#116b55',
      color_texto: '#17211d',
      color_header: '#ffffff',
      color_footer: '#10263a',
      color_fondo: '#f7f5ef',
      color_tarjetas: '#ffffff',
    },
  },
  {
    id: 'escritorio',
    nombre: 'Escritorio',
    plan: null,
    paleta: {
      color_primario: '#2563eb',
      color_secundario: '#7c3aed',
      color_botones: '#2563eb',
      color_texto: '#0f172a',
      color_header: '#ffffff',
      color_footer: '#0f172a',
      color_fondo: '#f8fafc',
      color_tarjetas: '#ffffff',
    },
  },
  {
    id: 'oscuro',
    nombre: 'Oscuro',
    plan: 'temas_varios',
    paleta: {
      color_primario: '#334155',
      color_secundario: '#0ea5e9',
      color_botones: '#0ea5e9',
      color_texto: '#e2e8f0',
      color_header: '#0f172a',
      color_footer: '#020617',
      color_fondo: '#0f172a',
      color_tarjetas: '#1e293b',
    },
  },
  {
    id: 'elegancia',
    nombre: 'Elegancia',
    plan: 'personalizacion_avanzada',
    paleta: {
      color_primario: '#9333ea',
      color_secundario: '#f59e0b',
      color_botones: '#9333ea',
      color_texto: '#1e1b2e',
      color_header: '#ffffff',
      color_footer: '#1e1b2e',
      color_fondo: '#fcfaff',
      color_tarjetas: '#ffffff',
    },
  },
  {
    id: 'naturaleza',
    nombre: 'Naturaleza',
    plan: 'personalizacion_avanzada',
    paleta: {
      color_primario: '#166534',
      color_secundario: '#d97706',
      color_botones: '#166534',
      color_texto: '#1c1917',
      color_header: '#f7fee7',
      color_footer: '#14532d',
      color_fondo: '#f7fee7',
      color_tarjetas: '#ffffff',
    },
  },
];

// ---------------------------------------------------------------------------
// Gating por plan
// ---------------------------------------------------------------------------
// Nivel mínimo legible para cada feature (para el copy "Disponible desde...").
// Se usa la feature de la matriz; si la empresa no la tiene, se bloquea.
const PLAN_MINIMO_FEATURE = {
  temas_varios: 'Medio',
  cabecera_avanzada: 'Medio',
  menu_avanzado: 'Medio',
  estilo_botones_avanzado: 'Medio',
  estilo_tarjetas_avanzado: 'Medio',
  tipografias_avanzadas: 'Medio',
  orden_secciones: 'Medio',
  secciones_portada_avanzadas: 'Medio',
  personalizacion_avanzada: 'Pro',
  quitar_branding: 'Pro',
};

export function planMinimoDeFeature(feature) {
  return PLAN_MINIMO_FEATURE[feature] ?? 'Medio';
}

/**
 * Evalúa el bloqueo de una opción según las features de la empresa.
 * @param {object} empresa - objeto empresa con plan.plan_feature
 * @param {string|null} feature - feature requerida (null = siempre libre)
 * @param {function} tieneFeature - (empresa, clave) => boolean (de @plataforma/utils)
 * @returns {{bloqueado:boolean, planMinimo:string|null}}
 */
export function evaluarBloqueo(empresa, feature, tieneFeature) {
  if (!feature) return { bloqueado: false, planMinimo: null };
  const esPro = Boolean(tieneFeature(empresa, 'personalizacion_avanzada'));
  // Las features de la matriz colapsan por nivel: si la empresa tiene
  // personalización avanzada (Pro) tiene también las de Medio, y si tiene
  // las de Medio hereda las de Prueba/Básico.
  const tiene = esPro || tieneFeature(empresa, feature);
  return {
    bloqueado: !tiene,
    planMinimo: planMinimoDeFeature(feature),
  };
}

// Helper puro para renderizar la etiqueta de bloqueo elegante.
export function etiquetaBloqueo(planMinimo) {
  return `${iconoCandado()} Disponible desde Plan ${planMinimo}`;
}

// Clave con que el storefront identificará la config del editor (para PARTE 2).
export const CLAVE_CONFIG_EDITOR = 'editor_tienda';
